<div class="flex flex-col gap-4">
  @for (card of cards; track card.type) {
    <div
      class="rounded-md border border-gray-200 bg-white p-4"
      [attr.data-testid]="'objective-card-' + card.type"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-3">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
          >
            <lucide-angular
              [img]="card.icon"
              class="h-5 w-5 text-primary-500"
            />
          </div>
          <div>
            <div class="flex items-center gap-1">
              <h3 class="text-paragraph-s-reg font-semibold text-greyscale-800">
                {{ card.labelKey | transloco }}
              </h3>
              @if (card.type === "AVG_EMISSION_PER_TRAVELLER") {
                <button
                  type="button"
                  class="flex h-5 w-5 items-center justify-center rounded-full text-greyscale-400 transition-colors hover:text-primary-500"
                  (click)="infoDialogVisible.set(true)"
                >
                  <lucide-angular
                    [img]="infoIcon"
                    class="h-4 w-4"
                  />
                </button>
              }
            </div>
            <p class="text-paragraph-xs-reg text-greyscale-500">
              {{ card.descriptionKey | transloco }}
            </p>
          </div>
        </div>
        <p-toggleSwitch
          [(ngModel)]="card.isActive"
          [attr.data-testid]="'objective-toggle-' + card.type"
        />
      </div>

      @if (card.isActive) {
        <div class="mt-4 flex flex-col gap-3">
          @if (getProgressForCard(card.type); as prog) {
            @if (prog.currentValue > 0) {
              <!-- Barre de progression condensée -->
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-3">
                  <p-progressBar
                    [value]="prog.progressPercent"
                    [showValue]="false"
                    styleClass="h-2"
                    class="flex-1"
                  />
                  @if (prog.daysLeft !== null && prog.daysLeft > 0) {
                    <div
                      class="flex flex-col items-end whitespace-nowrap text-right"
                    >
                      <span class="text-xs font-semibold text-greyscale-600">
                        {{
                          "objectives.daysLeftCount"
                            | transloco: { days: prog.daysLeft }
                        }}
                      </span>
                      <span class="text-xs text-greyscale-400">
                        {{ "objectives.daysLeftLabel" | transloco }}
                      </span>
                    </div>
                  }
                </div>
              </div>
              <!-- 3 chips -->
              <div class="flex items-stretch gap-3">
                <!-- Chip 1 : Actuel -->
                <div
                  class="flex flex-1 flex-col rounded-md border border-gray-200 bg-gray-50 p-3"
                >
                  <span
                    class="text-xs font-medium uppercase text-greyscale-400"
                    >{{ "objectives.chipCurrent" | transloco }}</span
                  >
                  <div class="flex items-baseline gap-1">
                    <span class="text-lg font-bold text-greyscale-800">{{
                      formatCo2eService.formatValue(prog.currentValue)
                    }}</span>
                    <span class="text-xs text-greyscale-500">{{
                      co2eUnit(prog.currentValue, card)
                    }}</span>
                  </div>
                </div>
                <!-- Flèche -->
                <div class="flex items-center text-greyscale-300">→</div>
                <!-- Chip 2 : Objectif -->
                <div
                  class="flex flex-1 flex-col rounded-md border border-primary-200 bg-primary-50 p-3"
                >
                  <span
                    class="text-xs font-medium uppercase text-primary-400"
                    >{{ "objectives.chipTarget" | transloco }}</span
                  >
                  <div class="flex items-baseline gap-1">
                    <span class="text-lg font-bold text-primary-800">{{
                      formatCo2eService.formatValue(prog.targetValue)
                    }}</span>
                    <span class="text-xs text-primary-500">{{
                      co2eUnit(prog.targetValue, card)
                    }}</span>
                  </div>
                </div>
                <!-- Chip 3 : Delta -->
                <div
                  class="flex flex-1 flex-col rounded-md border p-3"
                  [class]="getDeltaChipClass(prog.progressPercent)"
                >
                  @if (prog.progressPercent >= 100) {
                    <span class="text-xs font-medium uppercase">{{
                      "objectives.chipGoalReached" | transloco
                    }}</span>
                    <div class="flex items-baseline gap-1">
                      <span class="text-lg font-bold">{{
                        formatCo2eService.formatValue(getDelta(prog))
                      }}</span>
                      <span class="text-xs">{{ co2eUnit(getDelta(prog), card) }}</span>
                    </div>
                  } @else {
                    <span class="text-xs font-medium uppercase">{{
                      "objectives.chipToReduce" | transloco
                    }}</span>
                    <div class="flex items-baseline justify-between">
                      <div class="flex items-baseline gap-1">
                        <span class="text-lg font-bold"
                          >▼ {{ formatCo2eService.formatValue(getDelta(prog)) }}</span
                        >
                        <span class="text-xs">{{ co2eUnit(getDelta(prog), card) }}</span>
                      </div>
                      <span class="text-lg"
                        >- {{ getDeltaPercent(prog) | number: "1.0-0" }}%</span
                      >
                    </div>
                  }
                </div>
              </div>
            } @else {
              @if (
                card.type === "EMISSION_PER_TRIP_PRICE" ||
                card.type === "EMISSION_PER_TRIP_REVENUE"
              ) {
                <p class="text-xs text-greyscale-400">
                  {{ "objectives.noPriceOnTrips" | transloco }}
                </p>
              } @else {
                <p class="text-xs text-greyscale-400">
                  {{ "objectives.noTripsYet" | transloco }}
                </p>
              }
            }
          }

          <!-- Inputs discrets séparés en bas -->
          <div class="mt-1 flex gap-3 border-t border-gray-100 pt-3">
            <div class="flex flex-1 flex-col gap-1">
              <label
                class="text-xs text-greyscale-500"
                [for]="'target-' + card.type"
              >
                {{ "objectives.targetValue" | transloco }}
              </label>
              <div class="flex items-center gap-2">
                <p-inputNumber
                  [id]="'target-' + card.type"
                  [(ngModel)]="card.targetValue"
                  [min]="0"
                  mode="decimal"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="2"
                  [placeholder]="'0'"
                  styleClass="w-full"
                  [attr.data-testid]="'objective-value-' + card.type"
                />
                <span class="whitespace-nowrap text-xs text-greyscale-400">{{
                  card.unitKey | transloco
                }}</span>
              </div>
            </div>

            <div class="flex flex-1 flex-col gap-1">
              <label
                class="text-xs text-greyscale-500"
                [for]="'year-' + card.type"
              >
                {{ "objectives.targetYear" | transloco }}
              </label>
              <p-inputNumber
                [id]="'year-' + card.type"
                [(ngModel)]="card.targetYear"
                [min]="currentYear"
                [useGrouping]="false"
                [placeholder]="'objectives.enterYear' | transloco"
                styleClass="w-full"
                [attr.data-testid]="'objective-year-' + card.type"
              />
            </div>

            @if (card.type === "EMISSION_REDUCTION_VS_YEAR") {
              <div class="flex flex-1 flex-col gap-1">
                <label
                  class="text-xs text-greyscale-500"
                  [for]="'baseline-' + card.type"
                >
                  {{ "objectives.baselineValue" | transloco }}
                </label>
                <div class="flex items-center gap-2">
                  <p-inputNumber
                    [id]="'baseline-' + card.type"
                    [(ngModel)]="card.baselineValue"
                    [min]="0"
                    mode="decimal"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="2"
                    [placeholder]="'0'"
                    styleClass="w-full"
                    [attr.data-testid]="'objective-baseline-' + card.type"
                  />
                  <span class="whitespace-nowrap text-xs text-greyscale-400">{{
                    "objectives.unitKgCo2e" | transloco
                  }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }

  <div class="mt-2">
    <p-button
      type="button"
      (click)="onSave()"
      [loading]="isLoading()"
      data-testid="objectives-save-button"
    >
      <lucide-angular [img]="targetIcon" class="mr-2 h-4 w-4" />
      <span class="font-semibold">{{ "objectives.save" | transloco }}</span>
    </p-button>
  </div>

  <p-dialog
    [(visible)]="infoDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '90%', maxWidth: '600px' }"
  >
    <ng-template pTemplate="header">
      <div class="flex items-center gap-3">
        <div
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-200"
        >
          <lucide-angular [img]="infoIcon" class="h-5 w-5 text-primary-500" />
        </div>
        <h2 class="text-heading-4 font-semibold text-primary-900">
          {{ "objectives.infoAvgEmissionTitle" | transloco }}
        </h2>
      </div>
    </ng-template>

    <div class="flex flex-col gap-4">
      <p class="text-paragraph-s-reg text-greyscale-600">
        {{ "objectives.infoAvgEmissionIntro" | transloco }}
      </p>

      <ul class="flex flex-col gap-2 pl-4">
        <li class="list-disc text-paragraph-s-reg text-greyscale-600">
          <span class="font-semibold">{{
            "objectives.infoAvgEmissionFitFor55" | transloco
          }}</span>
        </li>
        <li class="list-disc text-paragraph-s-reg text-greyscale-600">
          <span class="font-semibold">{{
            "objectives.infoAvgEmissionSbti" | transloco
          }}</span>
        </li>
        <li class="list-disc text-paragraph-s-reg text-greyscale-600">
          <span class="font-semibold">{{
            "objectives.infoAvgEmissionNetZero" | transloco
          }}</span>
        </li>
      </ul>

      <div class="rounded-lg border border-primary-200 bg-primary-50 p-4">
        <p class="text-paragraph-s-reg text-greyscale-600">
          {{ "objectives.infoAvgEmissionExample" | transloco }}
        </p>
      </div>

      <p class="text-paragraph-s-reg text-greyscale-600">
        {{ "objectives.infoAvgEmissionMore" | transloco }}
        <a
          [routerLink]="helpResourcesRoute"
          (click)="infoDialogVisible.set(false)"
          class="font-medium text-primary-500 underline underline-offset-2 hover:text-primary-600"
          >{{ "objectives.infoAvgEmissionMoreLink" | transloco }}</a
        >.
      </p>
    </div>
  </p-dialog>
</div>
